<template>
  <div>
    <v-dialog v-model="value" max-width="1300" scrollable persistent>
      <v-card outlined :loading="isLoading" style="min-height: 200px;">
        <v-overlay absolute v-if="isLoading" opacity="1" color="white">
          <v-progress-circular indeterminate color="green" size="70"></v-progress-circular>
        </v-overlay>
        <v-card-title class="d-flex justify-space-between align-center primary white--text">
          <div> {{ $t('payment.order') }} </div>
          <v-btn color="white" icon @click="$emit('input', false);">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="!isLoading">
          <div class="flex align-center gap-2 darkprimary--text mt-3 mb-3">
            <h3 class="font-weight-bold">{{ $t('meter.title') }}{{ meter && meter.isMain ? $t('main') : $t('additional')  }}</h3>
            <div>
              <v-chip class="chip-status info" v-if="meter && meter.digitalMeterData.type == 'prepaid'">
                {{ $t('prepaid') }}
              </v-chip>
            </div>
            <h3>({{ $t('area.space') }} {{ meter && meter.area.name ? meter.area.name : '-' }})</h3>
          </div>
          <v-divider></v-divider>
          <div class="flex items-center justify-between my-3">
            <h3 class="black--text font-weight-bold">{{ $t('payment.order_list') }}</h3>
            <div class="flex align-center gap-2" style="max-width: 500px;">
              <v-menu
                v-model="menuStartDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="$helper.datelabel(startDate)"
                    :placeholder="$t('date')"
                    outlined
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :label="$t('from_date')"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  no-title
                  locale="th"
                  @input="menuStartDate = false"
                  :header-date-format="$helper.headerDateFormat"
                  :year-format="$helper.yearFormat"
                  :weekday-format="$helper.formatWeekday" :month-format="$helper.formatMonth"
                ></v-date-picker>
              </v-menu>
              <v-menu
                v-model="menuEndDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="$helper.datelabel(endDate)"
                    :placeholder="$t('date')"
                    outlined
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :label="$t('to_date')"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="endDate"
                  no-title
                  locale="th"
                  @input="menuEndDate = false"
                  :header-date-format="$helper.headerDateFormat"
                  :year-format="$helper.yearFormat"
                  :weekday-format="$helper.formatWeekday" :month-format="$helper.formatMonth"
                ></v-date-picker>
              </v-menu>
              <v-btn @click="onSearch()" class="primary" :disabled="isLoading || isOrderDataLoading">
                <v-icon>mdi-magnify</v-icon> {{ $t('search') }}
              </v-btn>
            </div>
          </div>
          <v-data-table
            :headers="headers"
            :items="orderData"
            :loading="isOrderDataLoading"
            :items-per-page="50"
            class="border-2 data-table-resize-header"
            :no-data-text="$t('noinfo')"
            :no-results-text="$t('no_matching_records_found')">

            <template v-slot:header.meter.label="{ header }">
              <div class="text-nowrap-table text-center" style="min-width:190px">{{ header.text }}</div>
            </template>
            <template v-slot:header.customerData.name="{ header }">
              <div class="text-nowrap-table text-center" style="min-width:100px">{{ header.text }}</div>
            </template>

            <template v-slot:body.prepend>
            <tr>
              <td></td>
              <td class="ps-0">
                <v-text-field v-model="filterData.meter" type="text" :label="$t('meter.meter_name')" solo dense hide-details clearable/>
              </td>
              <td class="ps-0">
                <v-text-field v-model="filterData.customerData" type="text" :label="$t('market.marchant_name')" solo dense hide-details clearable/>
              </td>
              <td class="ps-0">
                <v-text-field v-model="filterData.documentId" type="text" :label="$t('market.document_id')" solo dense hide-details clearable/>
              </td>
              <td class="ps-0">
                <v-text-field v-model="filterData.totalUnit" type="text" :label="$t('meter.number_of_units')" solo dense hide-details clearable/>
              </td>
              <td class="ps-0">
                <v-text-field v-model="filterData.totalValue" type="text" :label="$t('payment.amount')" solo dense hide-details clearable/>
              </td>
              <td class="ps-0">
                <v-menu
                  v-model="menuDate"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="filterDateLabel(filterData.date)"
                      :label="$t('payment.order_date')"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      solo
                      dense
                      hide-details
                      :append-icon="filterData.date ? 'mdi-close' : null"
                      @click:append="filterData.date = null"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filterData.date"
                    @input="menuDate = false"
                    :header-date-format="$helper.headerDateFormat"
                    :year-format="$helper.yearFormat"
                    no-title
                    locale="th"
                    :weekday-format="$helper.formatWeekday" :month-format="$helper.formatMonth"
                  ></v-date-picker>
                </v-menu>
              </td>
              <td>
                <v-select v-model="filterData.status" item-text="text"
                  item-value="value" :items="filterOption.status" type="text" :label="$t('status')" solo dense
                  hide-details
                  clearable
                  :no-data-text="$t('noinfo')">
                </v-select>
              </td>
              <td></td>
            </tr>
          </template>
  
            <template v-slot:item.order="{ index }">
              <div  class="text-center">{{ index + 1 }}</div>
            </template>
            <template v-slot:item.meter.label="{ item }">
              <div class=" text-truncate">
                <div class="d-flex align-center">
                  <div> {{ item.meter.label }}</div>
                  <div v-if="item.meter.digitalMeterData.type == 'prepaid'" class="ms-2">
                    <v-chip class="chip-status info" >
                      <div class="chip-text-sm">{{ $t('prepaid') }}</div>
                    </v-chip>
                  </div>
                </div>
              </div>
            </template>

            <template v-slot:item.customerData.name="{ item }">
                <div class="text-truncate">
                  {{ item.customerData.name }}
                </div>
            </template>
  
            <template v-slot:item.documentId="{ item }">
              <div  class="text-center">{{ item.documentId ? item.documentId : '-' }}</div>
            </template>

            <template v-slot:item.status.text="{ item }">
              <template v-if="item.status.value == 'isPaid'">
                <v-chip class="chip-status success">
                  <v-icon left x-small> mdi-circle </v-icon>
                  {{ $t('finish') }}
                </v-chip>
              </template>
              <template v-else-if="item.status.value == 'overdue'">
                  <v-chip class="chip-status error">
                    <v-icon left x-small> mdi-circle </v-icon>
                    {{ $t('payment.pending') }}
                  </v-chip>
              </template>
              <template v-else-if="item.status.value == 'cancel'">
                  <v-chip class="chip-status error">
                    <v-icon left x-small> mdi-circle </v-icon>
                    {{ $t('cancel') }}
                  </v-chip>
              </template>
              <template v-else-if="item.status.value == 'pending'">
                  <v-chip class="chip-status warning">
                    <v-icon left x-small> mdi-circle </v-icon>
                    {{ $t('customer.waiting_confirm') }}
                  </v-chip>
              </template>
            </template>
  
            <template v-slot:item.totalUnit="{ item }">
              <div class="text-right">
                 {{ $helper.toCurrency(item.totalUnit) }}
              </div>
            </template>
  
  
            <template v-slot:item.totalValue="{ item }">
              <div class="text-right">
                {{ $helper.currencySign(item.currencyCode || 'THB') }} {{ $helper.toCurrency(item.totalValue) }}
              </div>
            </template>

            <template v-slot:item.date="{ item }">
              {{ $moment(item.date).format('DD/MM/YYYY, HH:mm ' + $t('timeunit.minute.short')) }}
            </template>
  
            <template v-slot:item.actions="{ item }">
              <div class="d-flex justify-center">
                <v-btn class="btn-detail-stall" small outlined elevation="0" @click="openOrderDetailDialog(item)">
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
        </v-card>
    </v-dialog>

    <MeterDigitalOrderDetailDialog 
      v-model="dialogMeterOrderDetail" 
      :orderId="orderId" 
      :projectId="projectId"
      @onSaveSuccess="getOrderData(),$emit('onSaveSuccess')"/>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import MeterDigitalOrderDetailDialog from "./MeterDigitalOrderDetailDialog.vue";
export default {
  components: {
    MeterDigitalOrderDetailDialog
  }, 
  computed: {
    filterOrderData(){
      return this.orderData.filter(item => item.documentId);
    }
  },
  props : {
    value : Boolean,
    areaId : String,
    meterId : String,
    projectId : String,
    marketId : String,
  },
  data(){
    return {
      isLoading: false,
      headers: [],
      menuStartDate: false,
      menuEndDate: false,
      startDate: this.$moment({ day: 1 }).format("YYYY-MM-DD"),
      endDate: this.$moment()
        .endOf("month")
        .format("YYYY-MM-DD"),
      filterData : {
        meter: "",
        customerData: "",
        documentId: "",
        totalUnit: "",
        totalValue: "",
        date: "",
        status: "",
      },
      filterOption: {
        status: [],
      },
      orderData: [],
      meter : null,
      dialogMeterOrderDetail : false,
      orderId: null,
      meterById : null,
      prepareOrderData : null,  
      isOrderDataLoading : false,
      menuDate : false,
    }
  },
  created(){
    this.headers = [
        {
          text: this.$t('rank'),
          align: "center",
          sortable: false,
          value: "order"
        },
        {
          text: this.$t('meter.meter_name'),
          align: "center whitespace-nowrap text-truncate hd-resize meter-name",
          class: "whitespace-nowrap",
          width: "1%",
          sortable: false,
          value: "meter.label",
          filter: (d) =>
            this.filterData.meter
              ? d.toLowerCase().includes(this.filterData.meter.toLowerCase())
              : d,
        },
        {
          text: this.$t('market.marchant_name'),
          align: "center whitespace-nowrap text-truncate hd-resize",
          class: "whitespace-nowrap",
          value: "customerData.name",
          width: "1%",
          sortable: false,
          filter: (d) =>
            this.filterData.customerData
              ? d.toLowerCase().includes(this.filterData.customerData.toLowerCase())
              : d,
        },
        {
          align: "center whitespace-nowrap",
          class: "whitespace-nowrap",
          value: "documentId",
          sortable: false,
          text: this.$t('payment.order_number'),
          filter: (d) => {
            if (this.filterData.documentId) {
              return d ? d.toLowerCase().includes(this.filterData.documentId.toLowerCase()) : false;
            }
            return true;
          }
        },
        {
          align: "center whitespace-nowrap",
          class: "whitespace-nowrap text-right",
          value: "totalUnit",
          sortable: false,
          text: this.$t('meter.number_of_units'),
          filter: d =>
              this.filterData.totalUnit
                ? d.toString().includes(this.filterData.totalUnit)
                : d || d === 0
        },
        {
          align: "center whitespace-nowrap",
          class: "whitespace-nowrap text-right",
          value: "totalValue",
          sortable: false,
          text: this.$t('payment.amount'),
          filter: d =>
              this.filterData.totalValue
                ? d.toString().includes(this.filterData.totalValue)
                : d || d === 0
        },
        {
          align: "center whitespace-nowrap",
          class: "whitespace-nowrap",
          value: "date",
          sortable: false,
          text: this.$t('payment.order_date'),
          filter: (d) => {
            if (this.filterData.date) {
              return d ? this.$moment(d).format('DD/MM/YYYY') == this.$moment(this.filterData.date).format('DD/MM/YYYY') : false;
            }
            return true;
          }
        },
        {
          align: "center whitespace-nowrap",
          class: "whitespace-nowrap",
          value: "status.text",
          sortable: false,
          text: this.$t('status'),
          width: "150px",
          filter: d =>
            this.filterData.status
              ? d == this.filterData.status
              : d
        },
        {
          width: "100px",
          text: this.$t('manage'),
          align: "center",
          sortable: false,
          value: "actions"
        }
    ];

    this.filterOption.status = [
        { text: this.$t('all'), value: '' },
        { text: this.$t('customer.waiting_confirm'), value: 'รอยืนยัน' },
        { text: this.$t('finish'), value: 'เสร็จสิ้น' },
        { text: this.$t('cancel'), value: 'ยกเลิก' },
        { text: this.$t('payment.pending'), value: 'ค้างชำระ' },
    ];
  },
  methods: {
    ...mapActions({
      getOrderBetweenDateByMeter : 'order/getOrderBetweenDateByMeter',
      getMeter : 'meter/getMeter'
    }),
    async initData(){
      this.isLoading = true
      try{
        this.meter = await this.getMeter(this.meterId)
        await this.getOrderData()
        this.isLoading = false
      }catch(err){
        this.isLoading = false
        this.$toast.show({
          content: err.message,
          color: "error"
        })
      }
    },
    async getOrderData(){
      this.isOrderDataLoading = true
      try{
        this.orderData = await this.getOrderBetweenDateByMeter({
          meterId : this.meterId,
          startDate : this.$moment(this.startDate).startOf('day').format('YYYY-MM-DD'),
          endDate : this.$moment(this.endDate).endOf('day').format('YYYY-MM-DD')
        })
        this.prepareData();
      }catch(err){
        this.$toast.show({
          content: err.message,
          color: "error"
        })
      }finally{
        this.isOrderDataLoading = false
      }
    },
    prepareData(){
     
      if(this.orderData){
        for(const order of this.orderData){
          order.date = order.date.iso;
          if(!order.documentId){
            order.documentId = '-'
          }
          if(order.meter){
            order.meter.label = order.meter.isMain && !order.meter.name ? this.$t('meter.main_meter') : order.meter.name ? order.meter.name : this.$t('meter.sub_meter')
          }else{
            order.meter = {
              label : '-'
            }
          }
          order.status = {
            text: '',
            value: ''
          };
          if(order.isPaid){
            order.status = {
              text: this.$t('finish'),
              value: 'isPaid'
            };
          }else if(!order.isPaid){
            if(!order.receipts && !order.isPaymentApprove){
              order.status = {
                text: this.$t('payment.pending'),
                value: 'overdue'
              };
            }else if(order.receipts){
              order.status = {
                text: this.$t('cancel'),
                value: 'cancel'
              };
            }else if(!order.receipts && order.isPaymentApprove){
              order.status = {
                text: this.$t('customer.waiting_confirm'),
                value: 'pending'
              };
            }
          }
        }
      }
    },
    openOrderDetailDialog(item){
      this.orderId = item.objectId;
      this.dialogMeterOrderDetail = true;
    },
    onSearch(){
      this.getOrderData();
    },
    filterDateLabel(date){
      if (date) {
        return this.$moment(date).format('DD MMM YYYY')
      } else {
        return ""
      }
    }
  },
  watch: {
    value(){
      if(this.value){
       this.initData();
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.btn-detail-stall {
  color: #595959;
}

.data-table-resize-header::v-deep th.custom-sort {
  position: relative;

  .v-icon {
    position: absolute;
    right: 10px;
    top: 15px;
  }
}

.data-table-resize-header::v-deep td.hd-resize {
  max-width: 100px;
  &.meter-name{
    max-width: 190px; 
  }
}
</style>








